import React, { ReactNode } from 'react';
import { OpeningHours } from '../../templates/components/localisation/utils/types';
import { ClosingTime } from '../../templates/components/availability/ClosingTime/ClosingTime';
import { DAYS_OF_WEEK } from '../../templates/components/store/utils/storeLayoutUtils';

export const getStoreTradingHours = (hours: Array<any> = [], day: 'today' | 'tomorrow') => {
  const closedText = 'Closed';
  const currentDay = new Date().getDay();
  let tradingHours = 'Contact store';
  if (hours.length) {
    if (day === 'today') {
      const today = hours.find(week => {
        return week.DayOfWeek === currentDay;
      });
      tradingHours = today.IsOpen ? `${today.OpeningTime} - ${today.ClosingTime}` : closedText;
    } else if (day === 'tomorrow') {
      const tomorrow = hours.find(week => {
        return week.DayOfWeek === (currentDay === 6 ? 0 : currentDay + 1);
      });
      tradingHours = tomorrow.IsOpen ? `${tomorrow.OpeningTime} - ${tomorrow.ClosingTime}` : closedText;
    }
  }
  return tradingHours;
};

const getTimeFromString = (date: string, time: string): Date => {
  const [datePart, timezonePart] = date.split('+');
  const [year, month, day] = datePart.split('T')[0].split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  let tzOffsetInMinutes = 0;
  if (timezonePart) {
    const [tzHours, tzMinutes] = timezonePart.split(':').map(Number);
    tzOffsetInMinutes = tzHours * 60 + (tzMinutes || 0);
  }

  const result = new Date(Date.UTC(year, month - 1, day, hours, minutes));
  result.setUTCMinutes(result.getUTCMinutes() - tzOffsetInMinutes);
  return result;
};

export const getStoreClosingTime = (now: Date, hours: Array<OpeningHours> = []): ReactNode => {
  const currentDay = now.getDay();
  let tradingHours: ReactNode = 'Closed';

  if (hours.length) {
    const today = hours.find(week => {
      return week.dayOfWeek === currentDay;
    });
    if (today?.isOpen) {
      const openingTime = getTimeFromString(today.date, today?.openingTime);
      if (openingTime && now < openingTime) {
        tradingHours = <ClosingTime isOpen={false} openingTime={today?.openingTime} />;
      } else {
        const closingTime = getTimeFromString(today.date, today?.closingTime);
        if (closingTime && now > closingTime) {
          tradingHours = getNextTradingHours(currentDay, hours);
        } else {
          tradingHours =
            today && today.isOpen && closingTime ? <ClosingTime isOpen={true} closingTime={today.closingTime} /> : '';
        }
      }
    }
  }
  return tradingHours;
};

const getNextTradingHours = (currentDay: number, hours: Array<OpeningHours>) => {
  const nextSevenDays = Array.from({ length: 7 }, (_, i) => (currentDay + i + 1) % 7);

  const nextOpenDay = nextSevenDays.find(day => {
    const dayHours = hours.find(week => week.dayOfWeek === day);
    return dayHours?.isOpen;
  });

  if (nextOpenDay === undefined) {
    return 'Contact store';
  }

  const nextDay = hours.find(week => week.dayOfWeek === nextOpenDay);
  const openingDay = nextOpenDay === nextSevenDays[0] ? 'tomorrow' : DAYS_OF_WEEK[nextOpenDay];

  return <ClosingTime isOpen={false} openingTime={nextDay!.openingTime} openingDay={openingDay} />;
};

const getToday = () => {
  return new Date();
};

export const getIfOpenForNextHour = (hours: Array<OpeningHours> = []) => {
  const current = getToday();
  const currentDay = current.getDay();
  const today = hours.find(week => {
    return week.dayOfWeek === currentDay;
  });
  if (today && today.isOpen) {
    const closingTime = getTimeFromString(today.date, today?.closingTime);
    const openingTime = getTimeFromString(today.date, today?.openingTime);

    const isStoreOpen = current > openingTime && current < closingTime;
    const isStoreClosingSoon = closingTime > new Date(current.getTime() + 60 * 60 * 1000);

    return isStoreOpen && isStoreClosingSoon;
  }
  return false;
};

export const getStoreClosingTimeFromFulfilmentResponse = (
  hours: Array<{
    day: string;
    startTime: string;
    endTime: string;
    isOpen?: boolean;
    areNormalTradingHours?: boolean;
    date?: string;
  }>
) => {
  const newHours = hours.map(hour => ({
    dayOfWeek: DAYS_OF_WEEK.indexOf(hour.day),
    openingTime: hour.startTime,
    closingTime: hour.endTime,
    isOpen: hour.isOpen ?? true,
    areNormalTradingHours: hour.areNormalTradingHours ?? true,
    date: hour.date ?? '',
  }));
  const tradingHours = getStoreClosingTime(new Date(), newHours);
  const isStoreOpenInNexthour = getIfOpenForNextHour(newHours);
  return { tradingHours, isStoreOpenInNexthour };
};
